import { useCallback, useEffect, useMemo, useState } from 'react';
import { toTitleCase } from '@/utils/to-title-case';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  Container,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { lighten } from '@mui/material/styles';
import {
  CalendarBlank as CalendarBlankIcon,
  ChatCircle as ChatCircleIcon,
  FileCsv as CsvIcon,
  Download as DownloadIcon,
  FileXls as ExcelIcon,
  Funnel as FunnelIcon,
  House as HouseIcon,
  PresentationChart as PresentationChartIcon,
  SortAscending as SortIcon,
  Trash as TrashIcon,
  User as UserIcon,
  UserPlus as UserPlusIcon,
} from '@phosphor-icons/react/dist/ssr';
import axios from 'axios';
import dayjs from 'dayjs';
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { useConfirm } from 'material-ui-confirm';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import * as XLSX from 'xlsx';

import type { Meeting } from '@/types/meeting';
import { config } from '@/config';
import { paths } from '@/paths';
import { useDialog } from '@/hooks/use-dialog';
import { CommunityPageHeader } from '@/components/common/community-page-header';

import CreateMeetingDialog from '../dialogs/CreateMeetingDialog';

function MeetingsList() {
  const [meetings, setMeetings] = useState<Meeting[] | null>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const createMeetingDialog = useDialog();
  const confirm = useConfirm();

  const navigate = useNavigate();

  // Helper functions
  const getMeetingAge = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Export functions
  const exportToCSV = () => {
    const csvData =
      meetings?.map((meeting) => ({
        'Meeting ID': meeting.id,
        'Meeting Type': meeting.meeting_type?.name || '',
        'Meeting Date': meeting.held_on ? dayjs(meeting.held_on).format('lll') : 'Not Scheduled',
        'Agenda': meeting.agenda || 'No Agenda',
        'Created By': meeting.created_by ? `${meeting.created_by.first_name} ${meeting.created_by.last_name}` : '',
        'Created At': formatDate(meeting.created_at),
        'Age (Days)': getMeetingAge(meeting.created_at),
        'Updated At': formatDate(meeting.updated_at),
      })) || [];

    const ws = XLSX.utils.json_to_sheet(csvData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Meetings');
    XLSX.writeFile(wb, `meetings_${new Date().toISOString().split('T')[0]}.csv`);
    setExportMenuAnchor(null);
  };

  const exportToExcel = () => {
    const excelData =
      meetings?.map((meeting) => ({
        'Meeting ID': meeting.id,
        'Meeting Type': meeting.meeting_type?.name || '',
        'Meeting Date': meeting.held_on ? dayjs(meeting.held_on).format('lll') : 'Not Scheduled',
        'Agenda': meeting.agenda || 'No Agenda',
        'Created By': meeting.created_by ? `${meeting.created_by.first_name} ${meeting.created_by.last_name}` : '',
        'Created At': formatDate(meeting.created_at),
        'Age (Days)': getMeetingAge(meeting.created_at),
        'Updated At': formatDate(meeting.updated_at),
      })) || [];

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Meetings');
    XLSX.writeFile(wb, `meetings_${new Date().toISOString().split('T')[0]}.xlsx`);
    setExportMenuAnchor(null);
  };

  const columns = useMemo<MRT_ColumnDef<Meeting>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        maxSize: 80,
        enableColumnFilter: false,
        enableSorting: true,
        muiTableHeadCellProps: ({ ...rest }) => ({
          ...rest,
          sx: {
            minWidth: 80,
            width: 80,
            maxWidth: 80,
          },
        }),
        accessorFn: (row) => {
          // Show first 8 characters of ID for better readability
          const shortId = row.id.toString().substring(0, 8);

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'help' }}>
              <Typography fontFamily="monospace" fontWeight={600} variant="body2">
                #{shortId}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: 'meeting_type',
        header: 'Meeting Type',
        size: 150,
        maxSize: 150,
        enableColumnFilter: true,
        enableSorting: true,
        filterVariant: 'select',
        filterSelectOptions:
          meetings
            ?.map((meeting) => meeting.meeting_type?.name)
            .filter(Boolean)
            .filter((value, index, self) => self.indexOf(value) === index)
            .map((type) => ({ text: toTitleCase(type || ''), value: type })) || [],
        muiTableHeadCellProps: ({ ...rest }) => ({
          ...rest,
          sx: {
            minWidth: 150,
            width: 150,
            maxWidth: 150,
          },
        }),
        accessorFn: (row) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip color="primary" label={toTitleCase(row.meeting_type?.name || '')} size="small" variant="outlined" />
          </Box>
        ),
      },
      {
        accessorKey: 'held_on',
        header: 'Meeting Date',
        size: 180,
        maxSize: 180,
        enableColumnFilter: true,
        enableSorting: true,
        filterVariant: 'date-range',
        muiTableHeadCellProps: ({ ...rest }) => ({
          ...rest,
          sx: {
            minWidth: 180,
            width: 180,
            maxWidth: 180,
          },
        }),
        accessorFn: (row) => (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <CalendarBlankIcon color="#666" size={16} />
              <Typography fontWeight={600} variant="body2">
                {row.held_on ? dayjs(row.held_on).format('MMM DD, YYYY') : 'Not Scheduled'}
              </Typography>
            </Box>
            {row.held_on ? (
              <Typography color="text.secondary" variant="caption">
                {dayjs(row.held_on).format('h:mm A')}
              </Typography>
            ) : null}
          </Box>
        ),
      },
      {
        accessorKey: 'agenda',
        header: 'Agenda',
        size: 300,
        maxSize: 300,
        grow: true,
        enableColumnFilter: true,
        enableSorting: true,
        filterVariant: 'text' as const,
        accessorFn: (row) => {
          const agenda = row.agenda || '';
          const hasAgenda = agenda.length > 0;

          return (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <ChatCircleIcon color="#666" size={16} />
                  <Typography fontWeight={600} variant="body2">
                    {hasAgenda ? 'Has Agenda' : 'No Agenda'}
                  </Typography>
                </Box>
                {hasAgenda ? (
                  <Typography
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    variant="caption"
                  >
                    {agenda.length > 100 ? `${agenda.slice(0, 100)}...` : agenda}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          );
        },
      },
      {
        accessorKey: 'created_by',
        header: 'Created By',
        size: 150,
        maxSize: 150,
        enableColumnFilter: true,
        enableSorting: true,
        filterVariant: 'text' as const,
        muiTableHeadCellProps: ({ ...rest }) => ({
          ...rest,
          sx: {
            minWidth: 150,
            width: 150,
            maxWidth: 150,
          },
        }),
        accessorFn: (row) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: 'primary.50',
                color: 'primary.main',
                border: '1px solid',
                borderColor: 'primary.200',
              }}
            >
              <UserIcon size={16} />
            </Avatar>
            <Box>
              <Typography color="text.primary" sx={{ fontWeight: 600, lineHeight: 1.2 }} variant="body2">
                {row.created_by ? `${row.created_by.first_name} ${row.created_by.last_name}` : 'Unknown'}
              </Typography>
              <Typography color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }} variant="caption">
                {row.created_by?.email || ''}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        size: 140,
        maxSize: 140,
        enableColumnFilter: false,
        enableSorting: true,
        accessorFn: (row) => {
          const age = getMeetingAge(row.created_at);
          const isRecent = age <= 7;

          return (
            <Box>
              <Typography sx={{ mb: 0.5 }} variant="body2">
                {formatDate(row.created_at)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarBlankIcon color={isRecent ? 'green' : 'gray'} size={14} />
                <Typography
                  color={isRecent ? 'success.main' : 'text.secondary'}
                  fontWeight={isRecent ? 600 : 400}
                  variant="caption"
                >
                  {age} day{age !== 1 ? 's' : ''} ago
                </Typography>
              </Box>
            </Box>
          );
        },
        muiTableHeadCellProps: ({ ...rest }) => ({
          ...rest,
          sx: {
            minWidth: 140,
            width: 140,
            maxWidth: 140,
          },
        }),
      },
    ],
    [meetings]
  );

  useEffect(() => {
    const getMeetings = async () => {
      try {
        const response = await axios.get(`${config.site.api}/api/v1/meetings`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });

        const data = response.data;
        setMeetings(data ?? []);
      } catch (error) {
        setMeetings([]);
        toast.error('Unable to fetch meetings');
      }
    };

    getMeetings();
  }, []);

  const handleMeetingDelete = useCallback((meetingId: number) => {
    confirm({
      title: 'Are you sure?',
      description: 'This will permanently delete the meeting.',
      cancellationText: 'Cancel',
      confirmationText: 'Delete',
    })
      .then(() => {
        axios.defaults.withCredentials = true;
        axios.get(`${config.site.api}/sanctum/csrf-cookie`);
        axios
          .delete(`${config.site.api}/api/v1/meetings/${meetingId}`, {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
          })
          .then(() => {
            toast.success('Meeting deleted successfully');
            setMeetings((meetings) => meetings?.filter((meeting) => meeting.id !== meetingId) ?? []);
          })
          .catch((error) => {
            console.error(error?.message);
            toast.error('Failed to delete meeting');
          });
      })
      .catch(() => {
        // do nothing
      });
  }, []);

  return (
    <>
      <Box
        component="main"
        sx={{ flexGrow: 1, py: 3, backgroundColor: (theme) => theme.palette.background.default, minHeight: '100vh' }}
      >
        <Container maxWidth="xl">
          <CommunityPageHeader
            
            icon={<PresentationChartIcon size={22} />}
            subtitle="View and manage meetings scheduled for your community"
            title="Meetings"
          />

          <Card sx={{ mt: 4 }}>
            <CardHeader
               title={
                <Box alignItems="center" display="flex" gap={2} justifyContent="space-between">
                  <Box display="flex" gap={2}>
                    <Button
                      onClick={createMeetingDialog.handleOpen}
                      size="small"
                      startIcon={<UserPlusIcon size={18} />}
                      sx={{ textTransform: 'none', fontWeight: 500 }}
                      variant="contained"
                    >
                      Create Meeting
                    </Button>
                  </Box>
                </Box>
              }
            />
            <Divider />

            <MaterialReactTable
              columns={columns}
              data={meetings ?? []}
              enableRowActions
              enableStickyHeader
              initialState={{
                showGlobalFilter: true,
                showColumnFilters: false,
                sorting: [{ id: 'created_at', desc: true }],
                density: 'compact',
              }}
              muiSearchTextFieldProps={{
                size: 'small',
                variant: 'outlined',
                placeholder: 'Search all meetings...',
              }}
              muiTableBodyCellProps={{
                sx: {
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
              }}
              muiTableBodyRowProps={({ row }) => ({
                onClick: (event) => {
                  const clickedElement = event.target as HTMLElement;
                  if (
                    clickedElement.classList.contains('MuiButton-root') ||
                    clickedElement.classList.contains('MuiSvgIcon-root')
                  ) {
                    return;
                  }
                  event.stopPropagation();
                  navigate(`${paths.meetings.index}/${row.original.id}`);
                },
                sx: {
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '& td': {
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  },
                },
              })}
              muiTableContainerProps={(props) => ({
                ...props,
                sx: {
                  minHeight: '500px',
                },
              })}
              muiTableHeadCellProps={{
                sx: {
                  backgroundColor: 'background.paper',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                },
              }}
              muiTableProps={{
                sx: {
                  tableLayout: 'fixed',
                },
              }}
              positionActionsColumn="last"
              renderRowActions={({ row }) => (
                <IconButton
                  color="error"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleMeetingDelete(row.original.id);
                  }}
                >
                  <TrashIcon size={18} />
                </IconButton>
              )}
              renderTopToolbar={({ table }) => (
                <Box
                  sx={(theme) => ({
                    backgroundColor: lighten(theme.palette.background.default, 0.05),
                    display: 'flex',
                    gap: '0.5rem',
                    p: '8px',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  })}
                >
                  <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <MRT_GlobalFilterTextField table={table} />
                    <MRT_ToggleFiltersButton table={table} />
                    <MRT_ShowHideColumnsButton table={table} />
                    <MRT_ToggleDensePaddingButton table={table} />
                  </Box>

                  <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {/* Export Menu */}
                    <Button
                      onClick={(event) => {
                        setExportMenuAnchor(event.currentTarget);
                      }}
                      size="small"
                      startIcon={<DownloadIcon size={18} />}
                      sx={{ textTransform: 'none', fontWeight: 500 }}
                      variant="outlined"
                    >
                      Export
                    </Button>
                    <Menu
                      anchorEl={exportMenuAnchor}
                      onClose={() => {
                        setExportMenuAnchor(null);
                      }}
                      open={Boolean(exportMenuAnchor)}
                    >
                      <MenuItem onClick={exportToCSV}>
                        <ListItemIcon>
                          <CsvIcon size={20} />
                        </ListItemIcon>
                        <ListItemText>Export to CSV</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={exportToExcel}>
                        <ListItemIcon>
                          <ExcelIcon size={20} />
                        </ListItemIcon>
                        <ListItemText>Export to Excel</ListItemText>
                      </MenuItem>
                    </Menu>
                  </Box>
                </Box>
              )}
              state={{
                isLoading: !meetings,
              }}
            />
          </Card>
        </Container>
      </Box>

      <CreateMeetingDialog onClose={createMeetingDialog.handleClose} open={createMeetingDialog.open} />
    </>
  );
}

export default MeetingsList;
